// webapp/frontend/src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import store from '@/store'
import LevelModelsPage from '@/views/LevelModelsPage.vue'

// Define routes that are scoped to a project comment 
const projectRoutes = [
  {
    path: 'dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'in-flight',
    name: 'InFlight',
    component: () => import('../views/InFlight.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'landed',
    name: 'Landed',
    component: () => import('../views/Landed.vue'),
    meta: { requiresAuth: true }
  },
  // MaturityRisk route temporarily disabled
  {
    path: 'engineering-items',
    name: 'EngineeringItems',
    component: () => import('../views/MaturityDynamics.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'systems',
    name: 'Disciplines',
    component: () => import('../views/SystemsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'subsystems',
    name: 'Subsystems',
    component: () => import('../views/SubsystemsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'dependencies',
    name: 'Dependencies',
    component: () => import('../views/Dependencies.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'users',
    name: 'Users',
    component: () => import('../views/UsersView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'actions',
    name: 'Actions',
    component: () => import('../views/ActionsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'actions/:id/:slug?',
    name: 'ActionPage',
    component: () => import('../views/dynamic/ActionPage.vue'),
    props: r => ({ id: parseInt(r.params.id), slug: r.params.slug }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      if (!to.params.slug || to.params.slug === 'undefined') {
        try {
          const action = await api.getActionById(to.params.id)
          if (action?.slug) {
            // Keep the project acronym in the URL
            return next({ 
              path: `/${to.params.projectAcronym}/actions/${action.id}/${action.slug}`, 
              replace: true 
            })
          }
        } catch (e) {
          console.error(e)
        }
      }
      next()
    }
  },
  {
    path: 'actions/name/:name',
    name: 'ActionByName',
    component: () => import('../views/dynamic/ActionPage.vue'),
    props: r => ({ name: r.params.name }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      try {
        const action = await api.getActionByName(to.params.name)
        if (action) {
          return next({ 
            path: `/${to.params.projectAcronym}/actions/${action.id}/${action.slug}`, 
            replace: true 
          })
        }
      } catch (err) {
        console.error(err)
      }
      next({ name: 'NotFound', replace: true })
    }
  },
  {
    path: 'import',
    name: 'Import',
    component: () => import('../views/ImportView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'debug/tree-stats',
    name: 'TreeStatsDebug',
    component: () => import('../views/debug/TreeStatsDebug.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: 'level-models',
    name: 'LevelModels',
    component: LevelModelsPage,
    meta: {
      title: 'Level Models',
      requiresAuth: true
    }
  },
  {
    path: 'systems/:id/:slug?',
    name: 'SystemPage',
    component: () => import('../views/dynamic/SystemPage.vue'),
    props: r => ({ id: parseInt(r.params.id), slug: r.params.slug }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      if (!to.params.slug || to.params.slug === 'undefined') {
        try {
          const sys = await api.getSystemById(to.params.id)
          if (sys?.slug) {
            return next({ 
              path: `/${to.params.projectAcronym}/systems/${sys.id}/${sys.slug}`, 
              replace: true 
            })
          }
        } catch (e) {
          console.error(e)
        }
      }
      next()
    }
  },
  {
    path: 'systems/name/:name',
    name: 'SystemByName',
    component: () => import('../views/dynamic/SystemPage.vue'),
    props: r => ({ name: r.params.name }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      try {
        const sys = await api.getSystemByName(to.params.name)
        if (sys) {
          return next({ 
            path: `/${to.params.projectAcronym}/systems/${sys.id}/${sys.slug}`, 
            replace: true 
          })
        }
      } catch (err) {
        console.error(err)
      }
      next({ name: 'NotFound', replace: true })
    }
  },
  {
    path: 'subsystems/:id/:slug?',
    name: 'SubsystemPage',
    component: () => import('../views/dynamic/SubsystemPage.vue'),
    props: r => ({ id: parseInt(r.params.id), slug: r.params.slug }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      if (!to.params.slug || to.params.slug === 'undefined') {
        try {
          const ss = await api.getSubsystemById(to.params.id)
          if (ss?.slug) {
            return next({ 
              path: `/${to.params.projectAcronym}/subsystems/${ss.id}/${ss.slug}`, 
              replace: true 
            })
          }
        } catch (e) {
          console.error(e)
        }
      }
      next()
    }
  },
  {
    path: 'subsystems/name/:name',
    name: 'SubsystemByName',
    component: () => import('../views/dynamic/SubsystemPage.vue'),
    props: r => ({ name: r.params.name }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      try {
        const ss = await api.getSubsystemByName(to.params.name)
        if (ss) {
          return next({ 
            path: `/${to.params.projectAcronym}/subsystems/${ss.id}/${ss.slug}`, 
            replace: true 
          })
        }
      } catch (err) {
        console.error(err)
      }
      next({ name: 'NotFound', replace: true })
    }
  },
  {
    path: 'devitems/:id/:slug?',  // Make slug optional
    name: 'DevItemPage',
    component: () => import('../views/dynamic/DevItemPage.vue'),
    props: route => ({
      id: parseInt(route.params.id),
      slug: route.params.slug
    }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      // Simplified routing like systems - no slug validation on backend
      if (!to.params.slug || to.params.slug === 'undefined') {
        try {
          const item = await api.getDevItemById(to.params.id)
          if (item?.slug) {
            return next({ 
              path: `/${to.params.projectAcronym}/devitems/${item.id}/${item.slug}`, 
              replace: true 
            })
          }
        } catch (e) {
          console.error(e)
        }
      }
      next()
    }
  },
  {
    path: 'devitems/name/:name',
    name: 'DevItemByName',
    component: () => import('../views/dynamic/DevItemPage.vue'),
    props: route => ({ name: route.params.name }),
    meta: { requiresAuth: true },
    beforeEnter: async (to, from, next) => {
      try {
        const item = await api.getDevItemByName(to.params.name)
        if (item) {
          // Redirect to canonical URL
          return next({
            path: `/${to.params.projectAcronym}/devitems/${item.id}/${item.slug}`,
            replace: true
          })
        }
        return next({
          name: 'NotFound',
          replace: true
        })
      } catch (error) {
        return next({
          name: 'NotFound',
          replace: true
        })
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('../views/LoginView.vue')
    },
    {
      path: '/profile',
      name: 'Profile',
      component: () => import('../views/ProfileView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/',
      name: 'Root',
      component: { template: '<div>Redirecting...</div>' }, // Temporary component while redirecting
      beforeEnter: async (to, from, next) => {
        // Logic to redirect to /:acronym/dashboard
        const auth = useAuthStore()
        if (!auth.isLoggedIn) {
          return next('/login')
        }

        // Ensure projects are loaded
        if (store.state.projects.projects.length === 0) {
          await store.dispatch('projects/fetchProjects')
        }

        // 1. Try last viewed project from user preference (if available in store/auth)
        const user = auth.user
        if (user && user.last_project_id) {
          targetProject = store.state.projects.projects.find(p => p.id === user.last_project_id)
        }
        
        // 2. Try localStorage
        const lastProjectId = localStorage.getItem('currentProjectId')
        let targetProject = null

        if (lastProjectId) {
          targetProject = store.state.projects.projects.find(p => p.id === parseInt(lastProjectId))
        }

        // 3. Fallback to first project
        if (!targetProject && store.state.projects.projects.length > 0) {
          targetProject = store.state.projects.projects[0]
        }

        // 4. Default fallback
        if (targetProject) {
          const acronym = targetProject.acronym || 'def' // Fallback acronym if missing
          return next(`/${acronym.toLowerCase()}/dashboard`)
        } else {
           // No projects found or loaded? Fallback to default
           return next('/def/dashboard')
        }
      }
    },
    {
      path: '/:projectAcronym',
      component: () => import('@/views/ProjectLayout.vue'),
      children: projectRoutes,
      beforeEnter: async (to, from, next) => {
        const acronym = to.params.projectAcronym
        
        // Ensure projects are loaded
        if (store.state.projects.projects.length === 0) {
          await store.dispatch('projects/fetchProjects')
        }

        const project = store.getters['projects/getProjectByAcronym'](acronym)

        if (project) {
          // If project found, ensure it's selected
          if (store.getters['projects/currentProjectId'] !== project.id) {
            await store.dispatch('projects/selectProject', project)
          }
          next()
        } else {
          console.warn(`Project with acronym ${acronym} not found. Redirecting to default.`)
          // Fallback to default project
          // Check if we have a default project (ID 1 or name 'Default Project') or just pick first
          // The plan says "Redirect to the default project selection page (always DEF)"
          // If we are already at /def/..., avoid infinite loop if 'def' doesn't exist in DB as acronym
          if (acronym.toLowerCase() === 'def') {
             // If we are here, it means 'def' acronym was not found in DB projects.
             // We should probably show 404 or try to load project ID 1 as 'def'
             // For now, let's just let it pass to 404 or specific error page if strict.
             // But user said "Fall back to /def/dashboard".
             // If we redirect to /def/dashboard and 'def' is not in DB, we loop.
             // We need to handle this.
             // Let's assume 'def' maps to the default project (likely ID 1).
             // If ID 1 exists but has no acronym, we might need to patch it or handle it here.
             // But for now, let's redirect to NotFound if 'def' is also invalid.
             next({ name: 'NotFound' })
          } else {
             next('/def/dashboard')
          }
        }
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('../views/NotFound.vue'),
      props: true
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  // Check if the route is public. 
  // Note: /:projectAcronym routes are not public generally, but we check specific paths.
  // The 'to.path' includes the acronym.
  // We can check route name or meta.
  
  const authRequired = to.matched.some(record => record.meta.requiresAuth)
  const auth = useAuthStore()

  console.log('Route guard:', {
    to: to.path,
    authRequired,
    hasToken: !!localStorage.getItem('token')
  })

  if (authRequired) {
    const isAuthenticated = await auth.verifyAuth()
    
    if (!isAuthenticated) {
      console.log('Not authenticated, redirecting to login')
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
    
    console.log('Authentication verified, proceeding to route')
    next()
  } else {
    next()
  }
})

export default router
